import { graphql, useStaticQuery } from "gatsby";
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  preFilterStandardPrograms
} from "../../utils/base";
import {
  numericSort,
  numericSortBy,
  sortBy,
  sortByDesc
} from "../../utils/commonFunctions";
import { flattenMenu } from "../../utils/flattenMenu";
import { formatNumberNoCurrency } from "../../utils/formatContent";
import ProgramApplyCard from "../cards/programApplyCard";
import FilterBox from "../filterBox";
import { ConditionalSwiper } from "../shared/conditionalSwiper";
import { FilterMenuContainer } from "../shared/filterMenuContainer";
import { getProgramIngoDetails } from "../shared/flex/programApplyCardIngoObject";

const FindProgramPage = ({
  groupProgram,
  currencyToUse,
  currencySymbol,
  preselectedProgramType,
  preselectedProgramFocus,
  preselectedProgramLocation
}) => {
  const filterBoxContainerRef = useRef(null);
  const [reformattedPrograms, setReformattedPrograms] = useState([]);
  const [filterOptions, setFilterOptions] = useState([]);
  const [mobileFilterOptions, setMobileFilterOptions] = useState([]);
  const [currentFilteredItems, setCurrentFilteredItems] = useState([]);

  const {
    stdPrograms,
    featuredIncludes,
    genericDetails,
    budgetDetails
  } = useStaticQuery(graphql`
    query {
      featuredIncludes: wp {
        acfOptionsWhatsIncluded {
          whatsIncluded {
            featuredWhatsIncluded {
              featuredWhatsinludedTitle {
                title
              }
              featuredWhatsincludedProductType {
                ... on WpProgramType {
                  slug
                }
              }
            }
          }
        }
      }

      stdPrograms: allWpProgram(
        # filter: { acfProgramsMeta: { programIngo: { ne: true } } }
        # filter: { acfProgramsMeta: { programJourneys: { ne: true } } }
        sort: { acfProgramsMeta: { programRating: DESC } }
      ) {
        nodes {
          ...programMiniDataFilterableFragment
        }
      }

      genericDetails: wp {
        ingoPrograms: acfOptionsIngoProgramsConfig {
          ingoPrograms {
            ingoRunAndLedByFlex
            ingoRunAndLedByGvi
          }
        }
      }
      budgetDetails: wp {
        acfOptionsIngoProgramsConfig {
          priceBudgetRangeFilters {
            budgetPricing {
              budgetPricingIsoCode
              budgetPricingOptions {
                budgetPricingOptionsLabel
                budgetPricingOptionsMax
                budgetPricingOptionsMin
              }
            }
          }
        }
      }
    }
  `);

  const runAndLedByGVi =
    genericDetails?.ingoPrograms?.ingoPrograms?.ingoRunAndLedByGvi;
  const runAndLedByFlex =
    genericDetails?.ingoPrograms?.ingoPrograms?.ingoRunAndLedByFlex;

  const programs =
    stdPrograms?.nodes?.filter((program) =>
      preFilterStandardPrograms(
        program,
        preselectedProgramType?.split(",") || [],
        [],
        preselectedProgramLocation?.split(",") || [],
        preselectedProgramFocus?.split(",") || []
      )
    ) ?? [];

  const budgetPricing =
    budgetDetails?.acfOptionsIngoProgramsConfig?.priceBudgetRangeFilters
      ?.budgetPricing ?? [];

  const currentPriceFilter = budgetPricing?.find(
    (filterItem) =>
      filterItem?.budgetPricingIsoCode?.toLowerCase() === currencyToUse ??
      process.env.GATSBY_PROJECT_CURRENCY ??
      "gbp"
  );

  const formatLabelWithCurrency = (label, currency) => {
    return label?.replace(
      /\d+([,-]\d+)?/g,
      (match) => `${currency}${formatNumberNoCurrency(match)}`
    );
  };

  const programPrices =
    currentPriceFilter?.budgetPricingOptions?.map((item) => {
      const label = formatLabelWithCurrency(
        item?.budgetPricingOptionsLabel,
        currencySymbol ?? ""
      );

      return {
        value: item?.budgetPricingOptionsLabel,
        label: label,
        min: item?.budgetPricingOptionsMin,
        max: item?.budgetPricingOptionsMax
      };
    }) ?? [];

  const initFormOptions = useCallback(async () => {
    const programLocations = [];
    const programTypes = [];
    const programFocuses = [];
    const programDuration = [];

    const reformattedPrograms = programs?.map((program) => {
      const locations = flattenMenu(program?.geographical?.nodes, {
        idKey: "id",
        parentKey: "parentId",
        childrenKey: "children"
      });

      const desktopLocation = locations?.flatMap((loc) => loc.children);
      desktopLocation.forEach((location) => {
        programLocations[location.slug] = {
          label: location.name,
          value: location.slug
        };
      });

      const focuses = program?.acfProgramsMeta?.programFocuses?.flatMap(
        (focus) => {
          programFocuses[focus.slug] = {
            label: focus.title,
            value: focus.slug
          };
          return focus.slug;
        }
      );

      const types = program?.acfProgramsMeta?.programTypes?.flatMap((type) => {
        programTypes[type.slug] = {
          label: /journey/gi.test(type.title)
            ? [
                type.title,
                `<div class="py-1 px-2 mx-2 bg-red-light planet:bg-green-standard text-white planet:text-gray-darkest text-xxxxs xl:text-xxs">NEW</div>`
              ].join("")
            : type.title,
          value: type.slug,
          rating: type?.acfProgramsType?.programTypeRating ?? 0
        };

        return type.slug;
      });

      const duration =
        program?.acfProgramsMeta?.durationDisplay
          ?.replaceAll(/\s|weeks|week/gi, "")
          ?.split("-")
          ?.map((v) => parseInt(v)) ?? "";

      const durationFilterArr =
        duration?.length > 1
          ? Array.from(
              { length: duration[1] - duration[0] + 1 },
              (_, i) => i + duration[0]
            )
          : duration ?? "";

      programDuration.push(durationFilterArr);

      const currentMaxPriceFilter =
        program.acfProgramsMeta?.minimumPriceFilter?.find(
          (filter) =>
            filter?.maxPriceFilterCurrencyIsoCode?.toLowerCase() ===
              currencyToUse ??
            process.env.GATSBY_PROJECT_CURRENCY ??
            "gbp"
        );

      const minPriceFilterAmount =
        currentMaxPriceFilter?.maxPriceFilterAmount ?? "";

      const minDisplayPriceFilterAmount =
        program?.programIngo || program?.acfProgramsMeta?.programIngo
          ? currentMaxPriceFilter?.maxPriceFilterDailyAmount
          : currentMaxPriceFilter?.maxPriceFilterAmount ?? "";

      return {
        id: program?.databaseId,
        title: program?.title,
        subtitle: program?.acfProgramsMeta?.programHeaderSubsectionTitle ?? "",
        programCode: program?.acfProgramsMeta?.programCode ?? "",
        content: program?.content ?? "",
        link: program?.uri,
        image: program?.featuredImage?.node?.localFile?.extension
          ? program?.featuredImage?.node?.localFile
          : null,
        sourceUrl: program?.featuredImage?.node?.sourceUrl,
        duration: program?.acfProgramsMeta?.durationDisplay ?? "",
        duration_filter: durationFilterArr,
        location: desktopLocation?.flatMap((loc) => loc.slug) ?? [],
        types: types ?? [],
        focus: focuses ?? [],
        // prices: prices ?? [],
        ingoFeaturedWhatsincluded:
          program?.acfProgramsMeta?.ingoFeaturedWhatsincluded ?? [],
        programPopular: program?.acfProgramsMeta?.programPopular ?? false,
        programIngo: program?.acfProgramsMeta?.programIngo ?? false,
        programJourneys: program?.acfProgramsMeta?.programJourneys ?? false,
        bioName: program?.acfProgramsMeta?.bioName ?? "",
        minimumPriceAmount: minDisplayPriceFilterAmount,
        programCapacity: program?.acfProgramsMeta?.programCapacity ?? "",
        minimumPrice: minPriceFilterAmount
          ? minPriceFilterAmount?.replaceAll(",", "")
          : 0,
        debugPriceFilter: program?.acfProgramsMeta?.minimumPriceFilter,
        currencyToUse
      };
    });

    setFilterOptions([
      {
        name: "programType",
        label: {
          prefix: "Select",
          body: "Program Types"
        },
        defaultValue:
          Object.values(programTypes)
            .filter((type) =>
              preselectedProgramType?.split(",")?.length > 1
                ? null
                : preselectedProgramType
                    ?.split(",")
                    ?.shift()
                    ?.includes(type.value)
            )
            .shift() || null,
        list: Object.values(programTypes).sort(sortByDesc("rating"))
      },
      {
        name: "programLocation",
        label: {
          prefix: "Choose",
          body: "Locations"
        },
        defaultValue:
          Object.values(programLocations)
            .filter((type) =>
              preselectedProgramLocation?.split(",").length > 1
                ? null
                : preselectedProgramLocation
                    ?.split(",")
                    ?.shift()
                    ?.includes(type.value)
            )
            .shift() || null,
        list: Object.values(programLocations).sort(sortBy("value"))
      },
      {
        name: "programPrice",
        label: {
          prefix: "Select",
          body: "Price ranges"
        },
        list: Object.values(programPrices).sort(numericSortBy("min"))
      },
      {
        name: "programFocus",
        label: {
          prefix: "Choose",
          body: "Program focuses"
        },
        defaultValue:
          Object.values(programFocuses)
            .filter((type) =>
              preselectedProgramFocus?.split(",").length > 1
                ? null
                : preselectedProgramFocus
                    ?.split(",")
                    ?.shift()
                    ?.includes(type.value)
            )
            .shift() || null,
        list: Object.values(programFocuses).sort(sortBy("value"))
      },
      {
        name: "programDuration",
        label: {
          prefix: "Select",
          body: "Durations"
        },
        list: Object.values(
          [...new Set(programDuration?.flatMap((u) => u))]
            .sort(numericSort)
            .map((t) => ({
              label: t > 1 ? `${t} weeks` : `${t} week`,
              value: t
            }))
        )
      }
    ]);
    setCurrentFilteredItems(reformattedPrograms);
    setReformattedPrograms(reformattedPrograms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currencyToUse,
    preselectedProgramType,
    preselectedProgramLocation,
    preselectedProgramFocus
  ]);

  useEffect(() => {
    const mobilemenu = filterOptions.map((menu) => {
      return {
        name: menu.name,
        label: menu.label.body,
        value: menu.value,
        defaultValue: menu.defaultValue ?? null,
        list: menu.list?.map((item) => ({ ...item, filter_name: menu.name }))
      };
    });

    setMobileFilterOptions(mobilemenu);
  }, [filterOptions]);

  useEffect(() => {
    initFormOptions();
  }, [initFormOptions]);

  const handleProgramFilter = useCallback(
    (objValues) => {
      const programTypeValue = objValues?.programType
        ? objValues?.programType?.value
        : "";
      const locationValue = objValues?.programLocation
        ? objValues?.programLocation?.value
        : "";
      const focusValue = objValues?.programFocus
        ? objValues?.programFocus?.value
        : "";
      const priceValue = objValues?.programPrice
        ? objValues?.programPrice?.value
        : "";
      const durationValue = objValues?.programDuration
        ? objValues?.programDuration?.value
        : "";

      if (objValues === "clear") {
        initFormOptions();
      } else if (
        programTypeValue ||
        locationValue ||
        focusValue ||
        priceValue ||
        durationValue
      ) {
        const filterConditions = {
          byLocation: (item) => item.location.includes(locationValue ?? ""),
          byProgramType: (item) => item.types.includes(programTypeValue ?? ""),
          byFocus: (item) => item.focus.includes(focusValue ?? ""),
          byDuration: (item) =>
            item.duration_filter.includes(durationValue ?? ""),
          byPrice: (item) => {
            // Waiting for the add logic, and actual dataset to be used
            const programPrice = parseInt(String(item.minimumPrice) ?? 0);
            const minimumThreshhold = parseInt(objValues?.programPrice?.min);
            const maximumThreshhold = parseInt(objValues?.programPrice?.max);

            return (
              programPrice >= minimumThreshhold &&
              programPrice <= maximumThreshhold
            );
          }
        };

        let selected = [];

        if (locationValue) {
          selected = [...selected, filterConditions.byLocation];
        }
        if (programTypeValue) {
          selected = [...selected, filterConditions.byProgramType];
        }
        if (focusValue) {
          selected = [...selected, filterConditions.byFocus];
        }
        if (priceValue) {
          selected = [...selected, filterConditions.byPrice];
        }
        if (durationValue) {
          selected = [...selected, filterConditions.byDuration];
        }

        const result = reformattedPrograms.filter((program) =>
          selected.every((f) => f(program))
        );
        setCurrentFilteredItems(result);
      } else if (
        objValues?.action === "change" &&
        (!programTypeValue ||
          !locationValue ||
          !focusValue ||
          !priceValue ||
          !durationValue)
      ) {
        initFormOptions();
      }
    },
    [reformattedPrograms, initFormOptions]
  );

  const featuredIncludesList =
    featuredIncludes?.acfOptionsWhatsIncluded?.whatsIncluded?.featuredWhatsIncluded
      ?.flatMap((itm) => itm)
      ?.map((item) => ({
        types: item?.featuredWhatsincludedProductType?.map(
          (lItem) => lItem?.slug
        ),
        features: item?.featuredWhatsinludedTitle?.map((featItem) => ({
          name: featItem?.title
        }))
      }));

  useEffect(() => {
    const handleScroll = () => {
      const filterContainerPosition =
        filterBoxContainerRef.current?.getBoundingClientRect().top;
      if (filterBoxContainerRef.current != null) {
        if (filterContainerPosition < window.innerHeight * 0.35 + 64) {
          filterBoxContainerRef.current.children[0].style.position = "fixed";
          filterBoxContainerRef.current.children[0].style.top = "64px";
          filterBoxContainerRef.current.children[0].style.right = 0;
        } else {
          filterBoxContainerRef.current.children[0].style.position = "absolute";
          filterBoxContainerRef.current.children[0].style.top = "-15vh";
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="bg-yellow-lighter planet:bg-gray-darker">
      <div className="w-full pb-10 mx-auto md:w-4/5 sm:pb-24 md:-mt-16">
        <div className="pb-12 absolute z-20 -top-[20%] w-full sm:relative sm:top-auto sm:w-auto hidden sm:block">
          <FilterBox
            filterLabel="Choose multiple options to filter by"
            options={filterOptions}
            filterCallback={handleProgramFilter}
          />
        </div>
        <div
          className="relative z-20 block w-11/12 mx-auto sm:hidden"
          ref={filterBoxContainerRef}
        >
          <FilterMenuContainer
            className="absolute -top-[15vh]"
            filterOptions={mobileFilterOptions}
            filterPrograms={handleProgramFilter}
          />
        </div>
        <div className="relative w-11/12 mx-auto md:w-full sticky-wrapper">
          <div className="w-full py-11 md:pt-0 md:pr-4">
            <div className="cards-wrapper h-full sm:h-[800px] overflow-y-scroll overflow-x-hidden sm:pr-4 md:p-5 md:-m-5 gvi-scrollbar-thin">
              <ConditionalSwiper
                condition={false}
                slidesPerView={1.3}
                spaceBetween={20}
                centeredSlides={false}
                className="h-full w-full"
              >
                {currentFilteredItems?.map((program, index) => {
                  const details = getProgramIngoDetails({
                    programIngo: false,
                    journeyProgram: program?.programJourneys,
                    additionalCardClass: program?.programIngo
                      ? "sm:h-64 sm:max-h-64"
                      : "sm:h-80 sm:max-h-80 2xl:h-96 2xl:max-h-96",
                    program,
                    customCardClass: true,
                    featuredIncludesList,
                    extraOptions: {
                      type: "apply",
                      target: "application",
                      labelClass:
                        "text-base leading-genericCta font-bold pr-2 text-white planet:text-black-light group-hover:text-black-light planet:group-hover:text-green-standard"
                    }
                  });

                  return (
                    <ProgramApplyCard
                      key={index}
                      {...details}
                      runAndLedByGVi={runAndLedByGVi}
                      runAndLedByFlex={runAndLedByFlex}
                    />
                  );
                })}
              </ConditionalSwiper>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindProgramPage;
